import React from 'react';
import { useUser } from '../hooks/useUser';

/**
 * Hidden Component - Conditionally renders children based on user privileges
 * 
 * This component provides a clean way to show/hide UI elements based on user permissions.
 * It integrates with the privilege system to ensure users only see what they're allowed to access.
 * 
 * Props:
 * - privilege: string | string[] - The privilege(s) required to show the content
 * - requireAll: boolean - If true, user must have ALL privileges when privilege is an array
 * - fallback: ReactNode - What to show when user doesn't have required privileges (default: null)
 * 
 * Usage examples:
 * 
 * // Single privilege - only system admins see this
 * <Hidden privilege="SYSTEM_ADMIN">
 *   <Button danger>Delete Everything</Button>
 * </Hidden>
 * 
 * // Multiple privileges - user needs ANY of these
 * <Hidden privilege={["MANAGE_USERS", "SYSTEM_ADMIN"]}>
 *   <Button>Manage Users</Button>
 * </Hidden>
 * 
 * // Multiple privileges - user needs ALL of these
 * <Hidden privilege={["VIEW_REPORTS", "EXPORT_REPORTS"]} requireAll={true}>
 *   <Button>Export Reports</Button>
 * </Hidden>
 * 
 * // With fallback content
 * <Hidden privilege="SYSTEM_ADMIN" fallback={<Text type="secondary">Access Denied</Text>}>
 *   <Button>Admin Panel</Button>
 * </Hidden>
 * 
 * // Using privilege constants
 * import { PRIVILEGES } from '../constants/privileges';
 * <Hidden privilege={PRIVILEGES.SYSTEM_ADMIN}>
 *   <AdminPanel />
 * </Hidden>
 */
const Hidden = ({ 
  children, 
  privilege, 
  requireAll = false, 
  fallback = null 
}) => {
  const { user, hasPrivilege } = useUser();

//console.log('Hidden component - user:', user);

 // alert(user ? `User logged in: ${user.name}` : 'No user logged in');
  // If no user is logged in, don't show anything
  if (!user) {
    return fallback;
  }

  // Handle single privilege
  if (typeof privilege === 'string') {
    return hasPrivilege(privilege) ? children : fallback;
  }

  // Handle array of privileges
  if (Array.isArray(privilege)) {
    if (requireAll) {
      // All privileges must be present
      const hasAllPrivileges = privilege.every(priv => hasPrivilege(priv));
      return hasAllPrivileges ? children : fallback;
    } else {
      // At least one privilege must be present
      const hasAnyPrivilege = privilege.some(priv => hasPrivilege(priv));
      return hasAnyPrivilege ? children : fallback;
    }
  }

  // If no privilege specified, show children
  return children;
};

export default Hidden;